import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PolicyEditor from '../../components/PolicyEditor';
import { 
  getMarinaSettings, 
  updateMarinaSettings
} from '../../firebase/services/marinaSettingsService';
import { MarinaSetting, BusinessHours } from '../../models/types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}



const MarinaSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Settings state
  const [settings, setSettings] = useState<MarinaSetting | null>(null);
  
  // Business info state
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    websiteUrl: ''
  });

  // Business hours state
  const [businessHours, setBusinessHours] = useState<BusinessHours>({
    monday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    tuesday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    wednesday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    thursday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    friday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    saturday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    sunday: { openTime: '09:00', closeTime: '17:00', isClosed: false }
  });

  // Seasonal hours state
  const [inSeasonHours, setInSeasonHours] = useState<BusinessHours>({
    monday: { openTime: '08:00', closeTime: '18:00', isClosed: false },
    tuesday: { openTime: '08:00', closeTime: '18:00', isClosed: false },
    wednesday: { openTime: '08:00', closeTime: '18:00', isClosed: false },
    thursday: { openTime: '08:00', closeTime: '18:00', isClosed: false },
    friday: { openTime: '08:00', closeTime: '19:00', isClosed: false },
    saturday: { openTime: '07:00', closeTime: '19:00', isClosed: false },
    sunday: { openTime: '07:00', closeTime: '18:00', isClosed: false }
  });

  const [outOfSeasonHours, setOutOfSeasonHours] = useState<BusinessHours>({
    monday: { openTime: '10:00', closeTime: '16:00', isClosed: false },
    tuesday: { openTime: '10:00', closeTime: '16:00', isClosed: false },
    wednesday: { openTime: '10:00', closeTime: '16:00', isClosed: false },
    thursday: { openTime: '10:00', closeTime: '16:00', isClosed: false },
    friday: { openTime: '10:00', closeTime: '17:00', isClosed: false },
    saturday: { openTime: '09:00', closeTime: '17:00', isClosed: false },
    sunday: { openTime: '09:00', closeTime: '16:00', isClosed: false }
  });

  // Seasonal settings state
  const [seasonalSettings, setSeasonalSettings] = useState({
    isSeasonalHoursEnabled: false,
    seasonStartDate: '05-01',
    seasonEndDate: '09-30'
  });

  // Reservation rules state
  const [reservationRules, setReservationRules] = useState({
    minReservationDuration: 2,
    maxReservationDuration: 8,
    advanceBookingDays: 30,
    minAdvanceBooking: 2,
    checkInTimeMinutes: 15,
    cancellationPolicy: '24-hour cancellation policy',
    cancellationDeadlineHours: 24,
    // New cancellation settings
    cancellationsAllowed: true,
    cancellationDurationRules: {
      enabled: false,
      rules: [] as Array<{ durationHours: number; deadlineHours: number }>
    },
    refundPolicy: 'Refunds will be processed according to our cancellation policy.',
    damageWaiverPolicy: 'By accepting our damage waiver, you limit your financial responsibility for accidental damage.',
    depositRequired: true,
    depositType: 'fixed' as 'fixed' | 'percentage',
    depositAmount: 50,
    baseRate: 100,
    salesTaxRate: 0
  });

  // Cancellation deadline UI state
  const [cancellationDeadlineValue, setCancellationDeadlineValue] = useState(24);
  const [cancellationDeadlineUnit, setCancellationDeadlineUnit] = useState<'hours' | 'days'>('hours');

  // Helper functions for cancellation deadline conversion
  const convertHoursToUIState = (hours: number) => {
    if (hours >= 24 && hours % 24 === 0) {
      setCancellationDeadlineValue(hours / 24);
      setCancellationDeadlineUnit('days');
    } else {
      setCancellationDeadlineValue(hours);
      setCancellationDeadlineUnit('hours');
    }
  };

  const convertUIStateToHours = (value: number, unit: 'hours' | 'days') => {
    return unit === 'days' ? value * 24 : value;
  };

  // Helper for day order
  const orderedDays: (keyof BusinessHours)[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const marinaSettings = await getMarinaSettings();
        setSettings(marinaSettings);
        
        // Populate business info
        setBusinessInfo({
          businessName: marinaSettings.businessName || '',
          address: marinaSettings.address || '',
          city: marinaSettings.city || '',
          state: marinaSettings.state || '',
          zipCode: marinaSettings.zipCode || '',
          phone: marinaSettings.phone || '',
          email: marinaSettings.email || '',
          websiteUrl: marinaSettings.websiteUrl || ''
        });

        // Populate business hours
        if (marinaSettings.businessHours) {
          setBusinessHours(marinaSettings.businessHours);
        }

        // Populate seasonal hours
        if (marinaSettings.inSeasonHours) {
          setInSeasonHours(marinaSettings.inSeasonHours);
        }
        if (marinaSettings.outOfSeasonHours) {
          setOutOfSeasonHours(marinaSettings.outOfSeasonHours);
        }

        // Populate seasonal settings
        setSeasonalSettings({
          isSeasonalHoursEnabled: marinaSettings.isSeasonalHoursEnabled || false,
          seasonStartDate: marinaSettings.seasonStartDate || '05-01',
          seasonEndDate: marinaSettings.seasonEndDate || '09-30'
        });

        // Populate reservation rules
        if (marinaSettings.reservationRules) {
          const rules = {
            minReservationDuration: marinaSettings.reservationRules.minReservationDuration || 2,
            maxReservationDuration: marinaSettings.reservationRules.maxReservationDuration || 8,
            advanceBookingDays: marinaSettings.reservationRules.advanceBookingDays || 30,
            minAdvanceBooking: marinaSettings.reservationRules.minAdvanceBooking || 2,
            checkInTimeMinutes: marinaSettings.reservationRules.checkInTimeMinutes || 15,
            cancellationPolicy: marinaSettings.reservationRules.cancellationPolicy || '24-hour cancellation policy',
            cancellationDeadlineHours: marinaSettings.reservationRules.cancellationDeadlineHours || 24,
            // New cancellation settings
            cancellationsAllowed: marinaSettings.reservationRules.cancellationsAllowed !== undefined ? marinaSettings.reservationRules.cancellationsAllowed : true,
            cancellationDurationRules: marinaSettings.reservationRules.cancellationDurationRules || {
              enabled: false,
              rules: []
            },
            refundPolicy: marinaSettings.reservationRules.refundPolicy || 'Refunds will be processed according to our cancellation policy.',
            damageWaiverPolicy: marinaSettings.reservationRules.damageWaiverPolicy || 'By accepting our damage waiver, you limit your financial responsibility for accidental damage.',
            depositRequired: marinaSettings.reservationRules.depositRequired || true,
            depositType: marinaSettings.reservationRules.depositType || 'fixed',
            depositAmount: marinaSettings.reservationRules.depositAmount || 50,
            baseRate: marinaSettings.reservationRules.baseRate || 100,
            salesTaxRate: marinaSettings.reservationRules.salesTaxRate || 0
          };
          
          setReservationRules(rules);
          
          // Set the UI state for cancellation deadline
          convertHoursToUIState(rules.cancellationDeadlineHours);
        }
        
      } catch (err) {
        console.error('Error loading marina settings:', err);
        setError('Failed to load marina settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(null);
      setError(null);

      const updatedSettings: Partial<MarinaSetting> = {
        ...businessInfo,
        businessHours,
        inSeasonHours,
        outOfSeasonHours,
        isSeasonalHoursEnabled: seasonalSettings.isSeasonalHoursEnabled,
        seasonStartDate: seasonalSettings.seasonStartDate,
        seasonEndDate: seasonalSettings.seasonEndDate,
        reservationRules: {
          ...reservationRules,
          cancellationDeadlineHours: convertUIStateToHours(cancellationDeadlineValue, cancellationDeadlineUnit)
        },
        updatedAt: new Date()
      };

      await updateMarinaSettings(updatedSettings);
      setSuccess('Marina settings updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error saving marina settings:', err);
      setError('Failed to save marina settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to render day hours
  const renderDayHours = (
    day: keyof BusinessHours, 
    hours: BusinessHours, 
    setHours: React.Dispatch<React.SetStateAction<BusinessHours>>,
    title: string
  ) => {
    const dayHours = hours[day];
    const dayString = String(day);
    
    return (
      <Grid container spacing={1} alignItems="center" key={`${title}-${dayString}`} sx={{ mb: 1 }}>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>
            {dayString}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <FormControlLabel
            control={
              <Switch
                checked={!dayHours.isClosed}
                onChange={(e) => {
                  setHours(prev => ({
                    ...prev,
                    [day]: {
                      ...prev[day],
                      isClosed: !e.target.checked
                    }
                  }));
                }}
                size="small"
              />
            }
            label="Open"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Open Time"
            type="time"
            value={dayHours.openTime}
            onChange={(e) => {
              setHours(prev => ({
                ...prev,
                [day]: {
                  ...prev[day],
                  openTime: e.target.value
                }
              }));
            }}
            disabled={dayHours.isClosed}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Close Time"
            type="time"
            value={dayHours.closeTime}
            onChange={(e) => {
              setHours(prev => ({
                ...prev,
                [day]: {
                  ...prev[day],
                  closeTime: e.target.value
                }
              }));
            }}
            disabled={dayHours.isClosed}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Marina Settings
        </Typography>
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Business Info" />
            <Tab label="Business Hours" />
            <Tab label="Seasonal Hours" />
            <Tab label="Reservation Rules" />
            <Tab label="Reservation Policies" />
          </Tabs>

          {/* Business Info Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Business Name"
                  value={businessInfo.businessName}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, businessName: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={businessInfo.phone}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, phone: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={businessInfo.email}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, email: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Website URL"
                  value={businessInfo.websiteUrl}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, websiteUrl: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={businessInfo.address}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="City"
                  value={businessInfo.city}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, city: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="State"
                  value={businessInfo.state}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, state: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="ZIP Code"
                  value={businessInfo.zipCode}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, zipCode: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Business Hours Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Regular Business Hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set your standard operating hours for each day of the week.
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {orderedDays.map((day) => 
                renderDayHours(day, businessHours, setBusinessHours, 'regular')
              )}
            </Box>
          </TabPanel>

          {/* Seasonal Hours Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={seasonalSettings.isSeasonalHoursEnabled}
                      onChange={(e) => setSeasonalSettings(prev => ({ 
                        ...prev, 
                        isSeasonalHoursEnabled: e.target.checked 
                      }))}
                    />
                  }
                  label="Enable Seasonal Hours"
                />
                <Typography variant="body2" color="text.secondary">
                  When enabled, different hours will be used during peak season vs off-season.
                </Typography>
              </Grid>

              {seasonalSettings.isSeasonalHoursEnabled && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Season Start Date (MM-DD)"
                      value={seasonalSettings.seasonStartDate}
                      onChange={(e) => setSeasonalSettings(prev => ({ 
                        ...prev, 
                        seasonStartDate: e.target.value 
                      }))}
                      placeholder="05-01"
                      fullWidth
                      helperText="Format: MM-DD (e.g., 05-01 for May 1st)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Season End Date (MM-DD)"
                      value={seasonalSettings.seasonEndDate}
                      onChange={(e) => setSeasonalSettings(prev => ({ 
                        ...prev, 
                        seasonEndDate: e.target.value 
                      }))}
                      placeholder="09-30"
                      fullWidth
                      helperText="Format: MM-DD (e.g., 09-30 for September 30th)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      In-Season Hours (Peak Season)
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {orderedDays.map((day) => 
                        renderDayHours(day, inSeasonHours, setInSeasonHours, 'in-season')
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Out-of-Season Hours (Off Season)
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {orderedDays.map((day) => 
                        renderDayHours(day, outOfSeasonHours, setOutOfSeasonHours, 'out-of-season')
                      )}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </TabPanel>

          {/* Reservation Rules Tab */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Reservation Duration (hours)"
                  type="number"
                  value={reservationRules.minReservationDuration}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    minReservationDuration: parseInt(e.target.value) || 1 
                  }))}
                  fullWidth
                  inputProps={{ min: 1, max: 24 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Maximum Reservation Duration (hours)"
                  type="number"
                  value={reservationRules.maxReservationDuration}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    maxReservationDuration: parseInt(e.target.value) || 8 
                  }))}
                  fullWidth
                  inputProps={{ min: 1, max: 24 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Advance Booking Window (days)"
                  type="number"
                  value={reservationRules.advanceBookingDays}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    advanceBookingDays: parseInt(e.target.value) || 30 
                  }))}
                  fullWidth
                  helperText="How far in advance customers can book"
                  inputProps={{ min: 1, max: 365 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Advance Notice (hours)"
                  type="number"
                  value={reservationRules.minAdvanceBooking}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    minAdvanceBooking: parseInt(e.target.value) || 2 
                  }))}
                  fullWidth
                  helperText="Minimum notice required before reservation time"
                  inputProps={{ min: 0, max: 48 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Check-in Time (minutes before)"
                  type="number"
                  value={reservationRules.checkInTimeMinutes}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    checkInTimeMinutes: parseInt(e.target.value) || 15 
                  }))}
                  fullWidth
                  helperText="How many minutes before the reservation should customers check-in"
                  inputProps={{ min: 0, max: 60 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Base Rate (per hour)"
                  type="number"
                  value={reservationRules.baseRate}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    baseRate: parseFloat(e.target.value) || 0 
                  }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Sales Tax Rate (%)"
                  type="number"
                  value={reservationRules.salesTaxRate}
                  onChange={(e) => setReservationRules(prev => ({ 
                    ...prev, 
                    salesTaxRate: parseFloat(e.target.value) || 0 
                  }))}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  helperText="REQUIRED: Must be configured for reservations to work. No fallback rates are used."
                  error={reservationRules.salesTaxRate === undefined || reservationRules.salesTaxRate === null}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={reservationRules.depositRequired}
                      onChange={(e) => setReservationRules(prev => ({ 
                        ...prev, 
                        depositRequired: e.target.checked 
                      }))}
                    />
                  }
                  label="Require Deposit"
                />
              </Grid>
              {reservationRules.depositRequired && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Deposit Type</InputLabel>
                      <Select
                        value={reservationRules.depositType}
                        onChange={(e) => setReservationRules(prev => ({ 
                          ...prev, 
                          depositType: e.target.value as 'fixed' | 'percentage' 
                        }))}
                        label="Deposit Type"
                      >
                        <MenuItem value="fixed">Fixed Amount</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={reservationRules.depositType === 'fixed' ? 'Deposit Amount ($)' : 'Deposit Percentage (%)'}
                      type="number"
                      value={reservationRules.depositAmount}
                      onChange={(e) => setReservationRules(prev => ({ 
                        ...prev, 
                        depositAmount: parseFloat(e.target.value) || 0 
                      }))}
                      fullWidth
                      inputProps={{ 
                        min: 0, 
                        max: reservationRules.depositType === 'percentage' ? 100 : undefined,
                        step: 0.01 
                      }}
                    />
                  </Grid>
                </>
              )}

                          </Grid>
            </TabPanel>

          {/* Reservation Policies Tab */}
          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={4}>
              {/* Cancellation Policy */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Cancellation Policy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure when cancellations are allowed and the policy text.
                  </Typography>
                  
                  {/* Allow Cancellations Toggle */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={reservationRules.cancellationsAllowed}
                            onChange={(e) => setReservationRules(prev => ({
                              ...prev,
                              cancellationsAllowed: e.target.checked
                            }))}
                          />
                        }
                        label="Allow Cancellations"
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        When disabled, customers will not be able to cancel their reservations online.
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Cancellation Rules - Only show if cancellations are allowed */}
                  {reservationRules.cancellationsAllowed && (
                    <>
                      {/* Duration-based Rules Toggle */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={reservationRules.cancellationDurationRules.enabled}
                                onChange={(e) => setReservationRules(prev => ({
                                  ...prev,
                                  cancellationDurationRules: {
                                    ...prev.cancellationDurationRules,
                                    enabled: e.target.checked
                                  }
                                }))}
                              />
                            }
                            label="Use Duration-Based Cancellation Rules"
                          />
                          <Typography variant="caption" display="block" color="text.secondary">
                            When enabled, different cancellation deadlines apply based on reservation duration.
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Simple Cancellation Deadline (when duration rules are disabled) */}
                      {!reservationRules.cancellationDurationRules.enabled && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={6}>
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>
                                Cancellation Deadline
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                  label="Value"
                                  type="number"
                                  value={cancellationDeadlineValue}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    setCancellationDeadlineValue(value);
                                    // Update the main state as well for real-time preview
                                    setReservationRules(prev => ({ 
                                      ...prev, 
                                      cancellationDeadlineHours: convertUIStateToHours(value, cancellationDeadlineUnit)
                                    }));
                                  }}
                                  size="small"
                                  inputProps={{ 
                                    min: 0, 
                                    max: cancellationDeadlineUnit === 'days' ? 30 : 720 
                                  }}
                                  sx={{ flex: 1 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel>Unit</InputLabel>
                                  <Select
                                    value={cancellationDeadlineUnit}
                                    onChange={(e) => {
                                      const unit = e.target.value as 'hours' | 'days';
                                      setCancellationDeadlineUnit(unit);
                                      // Update the main state as well for real-time preview
                                      setReservationRules(prev => ({ 
                                        ...prev, 
                                        cancellationDeadlineHours: convertUIStateToHours(cancellationDeadlineValue, unit)
                                      }));
                                    }}
                                    label="Unit"
                                  >
                                    <MenuItem value="hours">Hours</MenuItem>
                                    <MenuItem value="days">Days</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                How long before reservation time cancellations are allowed
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                      {/* Duration-based Rules UI */}
                      {reservationRules.cancellationDurationRules.enabled && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Duration-Based Cancellation Rules
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 2, color: 'text.secondary' }}>
                              Configure different cancellation deadlines based on reservation duration.
                            </Typography>
                            
                            {/* Rules List */}
                            {reservationRules.cancellationDurationRules.rules.map((rule, index) => (
                              <Box key={index} sx={{ 
                                border: '1px solid', 
                                borderColor: 'divider', 
                                borderRadius: 1, 
                                p: 2, 
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                              }}>
                                <TextField
                                  label="Minimum Duration (hours)"
                                  type="number"
                                  value={rule.durationHours}
                                  onChange={(e) => {
                                    const newRules = [...reservationRules.cancellationDurationRules.rules];
                                    newRules[index] = { ...rule, durationHours: parseInt(e.target.value) || 0 };
                                    setReservationRules(prev => ({
                                      ...prev,
                                      cancellationDurationRules: {
                                        ...prev.cancellationDurationRules,
                                        rules: newRules
                                      }
                                    }));
                                  }}
                                  size="small"
                                  inputProps={{ min: 1 }}
                                  sx={{ minWidth: 200 }}
                                />
                                <TextField
                                  label="Cancellation Deadline (hours)"
                                  type="number"
                                  value={rule.deadlineHours}
                                  onChange={(e) => {
                                    const newRules = [...reservationRules.cancellationDurationRules.rules];
                                    newRules[index] = { ...rule, deadlineHours: parseInt(e.target.value) || 0 };
                                    setReservationRules(prev => ({
                                      ...prev,
                                      cancellationDurationRules: {
                                        ...prev.cancellationDurationRules,
                                        rules: newRules
                                      }
                                    }));
                                  }}
                                  size="small"
                                  inputProps={{ min: 0 }}
                                  sx={{ minWidth: 200 }}
                                />
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    const newRules = reservationRules.cancellationDurationRules.rules.filter((_, i) => i !== index);
                                    setReservationRules(prev => ({
                                      ...prev,
                                      cancellationDurationRules: {
                                        ...prev.cancellationDurationRules,
                                        rules: newRules
                                      }
                                    }));
                                  }}
                                >
                                  Remove
                                </Button>
                              </Box>
                            ))}
                            
                            <Button
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={() => {
                                setReservationRules(prev => ({
                                  ...prev,
                                  cancellationDurationRules: {
                                    ...prev.cancellationDurationRules,
                                    rules: [
                                      ...prev.cancellationDurationRules.rules,
                                      { durationHours: 4, deadlineHours: 24 }
                                    ]
                                  }
                                }));
                              }}
                              sx={{ mt: 1 }}
                            >
                              Add Rule
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}

                  <Typography variant="subtitle2" gutterBottom>
                    Cancellation Policy Text
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                    Detailed cancellation policy text shown to customers. You can use formatting like <strong>bold</strong>, <em>italic</em>, and lists. 
                    Available variables are shown below the editor for easy copy/paste.
                  </Typography>
                  <PolicyEditor
                    value={reservationRules.cancellationPolicy}
                    onChange={(value) => setReservationRules(prev => ({ 
                      ...prev, 
                      cancellationPolicy: value 
                    }))}
                    placeholder="Enter your detailed cancellation policy here..."
                    variables={[
                      {
                        key: 'cancellationDeadline',
                        label: 'Cancellation Deadline',
                        description: 'How long before reservation time cancellations are allowed'
                      },
                      {
                        key: 'depositAmount',
                        label: 'Deposit Amount',
                        description: 'Required deposit amount or percentage'
                      },
                      {
                        key: 'depositType',
                        label: 'Deposit Type',
                        description: 'Whether deposit is fixed amount or percentage'
                      },
                      {
                        key: 'baseRate',
                        label: 'Base Rate',
                        description: 'Base hourly rate for reservations'
                      },
                      {
                        key: 'salesTaxRate',
                        label: 'Sales Tax Rate',
                        description: 'Applied sales tax percentage'
                      },
                      {
                        key: 'businessName',
                        label: 'Business Name',
                        description: 'Name of your marina/business'
                      },
                      {
                        key: 'businessPhone',
                        label: 'Business Phone',
                        description: 'Contact phone number'
                      },
                      {
                        key: 'businessEmail',
                        label: 'Business Email',
                        description: 'Contact email address'
                      }
                    ]}
                  />
                </Box>
              </Grid>

              {/* Refund Policy */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Refund Policy
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    Refund Policy Text
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                    Detailed refund policy text shown to customers. You can use formatting and variables.
                  </Typography>
                  <PolicyEditor
                    value={reservationRules.refundPolicy}
                    onChange={(value) => setReservationRules(prev => ({ 
                      ...prev, 
                      refundPolicy: value 
                    }))}
                    placeholder="Enter your detailed refund policy here..."
                    variables={[
                      {
                        key: 'cancellationDeadline',
                        label: 'Cancellation Deadline',
                        description: 'How long before reservation time cancellations are allowed'
                      },
                      {
                        key: 'depositAmount',
                        label: 'Deposit Amount',
                        description: 'Required deposit amount or percentage'
                      },
                      {
                        key: 'businessName',
                        label: 'Business Name',
                        description: 'Name of your marina/business'
                      },
                      {
                        key: 'businessPhone',
                        label: 'Business Phone',
                        description: 'Contact phone number'
                      },
                      {
                        key: 'businessEmail',
                        label: 'Business Email',
                        description: 'Contact email address'
                      }
                    ]}
                  />
                </Box>
              </Grid>

              {/* Damage Waiver Policy */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Damage Waiver Policy
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    Damage Waiver Policy Text
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                    Detailed damage waiver policy text shown to customers. Explain coverage and limitations.
                  </Typography>
                  <PolicyEditor
                    value={reservationRules.damageWaiverPolicy}
                    onChange={(value) => setReservationRules(prev => ({ 
                      ...prev, 
                      damageWaiverPolicy: value 
                    }))}
                    placeholder="Enter your detailed damage waiver policy here..."
                    variables={[
                      {
                        key: 'businessName',
                        label: 'Business Name',
                        description: 'Name of your marina/business'
                      },
                      {
                        key: 'businessPhone',
                        label: 'Business Phone',
                        description: 'Contact phone number'
                      },
                      {
                        key: 'businessEmail',
                        label: 'Business Email',
                        description: 'Contact email address'
                      }
                    ]}
                  />
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default MarinaSettingsPage; 